"use client";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { motion } from "framer-motion";
import Link from "next/link";
import React from "react";
import { Autoplay, EffectFlip, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import CustomImage from "@/components/shared/CustomImage";
import Title from "@/components/shared/Title";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/hooks/useTranslation";
import type { Home, Media } from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";
import type { PayLoadData } from "@/utils/payloadUtil";

const CityHighlight = ({ home }: { home: PayLoadData<Home> | undefined }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  return (
    <section
      id="city-highlight"
      className="overflow-hidden bg-gradient-to-b from-primary-50 to-white"
    >
      <div className="mx-auto xl:container">
        <Title
          title={home?.[language]?.cityHighlight?.title || ""}
          desc={home?.[language]?.cityHighlight?.desc || ""}
        />
        {(() => {
          const data =
            home?.[language]?.cityHighlight?.contentCityHighlight || [];

          return data.map((item, idx: number) => (
            <div
              key={item.id}
              className="mb-40 grid grid-cols-1 items-center gap-10 last:mb-0 lg:grid-cols-3 lg:gap-20"
            >
              <div
                className={`col-span-1 lg:col-span-2 ${idx % 2 === 1 ? "lg:order-2" : "lg:order-1"}`}
              >
                <Swiper
                  loop
                  effect="slide"
                  slidesPerView={1}
                  speed={900}
                  autoplay={{
                    delay: 3000,
                  }}
                  navigation
                  pagination={{ clickable: true }}
                  modules={[EffectFlip, Navigation, Pagination, Autoplay]}
                  className="custom-navigation custom-pagination hidden-pc pagination-mobile light aspect-square w-full md:aspect-video"
                >
                  {item.slide?.map((slide, slideIdx) => (
                    <SwiperSlide key={slide.id}>
                      <div key={slide.id || slideIdx}>
                        <CustomImage
                          src={getImageUrl(slide.image)}
                          alt={
                            (slide.image as Media)?.alt ||
                            "image city highlight"
                          }
                          fill
                          className="size-full object-cover object-top"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
              <motion.div
                initial={{ x: idx % 2 === 1 ? -100 : 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 * idx, duration: 1, ease: "easeOut" }}
                className={`col-span-1 mt-10 flex flex-col items-start px-5 lg:mt-0 ${idx % 2 === 1 ? "lg:order-1 lg:items-end lg:text-right" : "lg:order-2 lg:items-start"}`}
              >
                <h2
                  className="mb-4 text-2xl font-bold md:text-3xl"
                  style={{ fontFamily: "inherit" }}
                >
                  {item.title}
                </h2>
                <p className="mb-6 text-lg text-gray-600">{item.desc}</p>
                <Link
                  href="/booking"
                  className="group inline-flex items-center justify-center rounded-full border border-primary-600 px-6 py-2.5 font-medium text-primary-600 transition-transform duration-500"
                >
                  <span>{t({ de: "Jetzt Anfragen", en: "Book now" })}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="ml-2 size-5 transition-transform duration-300 group-hover:translate-x-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Link>
              </motion.div>
            </div>
          ));
        })()}
      </div>
    </section>
  );
};

export default CityHighlight;
